export type FiscalComprobanteExportItem = {
  label: string;
  quantity: number;
  price: number;
  sku?: string | null;
  unitOfMeasure?: string | null;
};

export type FiscalComprobanteExportInput = {
  saleId: string;
  saleRef: string;
  saleTotal: number;
  saleCreatedAt: string;
  items: FiscalComprobanteExportItem[];
  cae: string;
  issuedAt?: string | null;
  caeExpiration?: string | null;
  qrUrl?: string | null;
  qrPayload?: string | null;
  invoiceType?: number | null;
  pointOfSale?: number | null;
  invoiceNumber?: number | null;
  buyerName?: string | null;
  buyerCuit?: string | null;
  emitterBusinessName?: string | null;
  emitterCuit?: string | null;
  netAmount?: number | null;
  ivaAmount?: number | null;
  diningTableName?: string | null;
  /** Métodos de cobro de la venta (reimpresión: Cond. venta Contado / cuenta corriente). */
  paymentMethodIds?: string[];
};

export type BuildAfipQrUrlOptions = {
  buyerCuit?: string | null;
  cae?: string | null;
  issuedAt?: string | null;
};

const AFIP_QR_BASE = 'https://www.afip.gob.ar/fe/qr/?p=';

export function encodeBase64Utf8(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  if (typeof btoa !== 'undefined') {
    return btoa(binary);
  }
  throw new Error('Codificación base64 no disponible.');
}

/** Tipo documento receptor AFIP: CUIT. */
const AFIP_QR_DOC_CUIT = 80;
/** Tipo documento receptor AFIP: consumidor final / sin identificar. */
export const AFIP_QR_DOC_CF = 99;

export type AfipQrPayloadRecord = {
  ver?: number;
  fecha?: string;
  cuit?: number;
  ptoVta?: number;
  tipoCmp?: number;
  nroCmp?: number;
  importe?: number;
  moneda?: string;
  ctz?: number;
  tipoDocRec?: number;
  nroDocRec?: number;
  tipoCodAut?: string;
  codAut?: number | string;
};

/** Fecha UTC YYYY-MM-DD — coincide con CbteFch histórico WSFE (pre-fix timezone). */
export function formatAfipQrDateUtc(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toISOString().slice(0, 10);
}

function resolveAfipQrFecha(
  parsed: AfipQrPayloadRecord,
  options?: BuildAfipQrUrlOptions
): string {
  if (parsed.fecha?.trim()) {
    return String(parsed.fecha).slice(0, 10);
  }
  if (options?.issuedAt) {
    return formatAfipQrDateUtc(options.issuedAt);
  }
  return '';
}

export function parseAfipQrCodAut(value: number | string | null | undefined): number {
  const digits = String(value ?? '').replace(/\D/g, '').slice(0, 14);
  return digits ? Number(digits) : 0;
}

export function resolveAfipQrRecipientDocFields(
  buyerCuit?: string | null
): { tipoDocRec: number; nroDocRec: number } {
  const digits = buyerCuit?.replace(/\D/g, '') ?? '';
  if (digits.length === 11) {
    return { tipoDocRec: AFIP_QR_DOC_CUIT, nroDocRec: Number(digits) };
  }
  return { tipoDocRec: AFIP_QR_DOC_CF, nroDocRec: 0 };
}

export function normalizeAfipQrPayloadRecord(
  parsed: AfipQrPayloadRecord,
  options?: BuildAfipQrUrlOptions
): AfipQrPayloadRecord {
  const recipient =
    parsed.tipoDocRec != null && parsed.nroDocRec != null
      ? { tipoDocRec: Number(parsed.tipoDocRec), nroDocRec: Number(parsed.nroDocRec) }
      : resolveAfipQrRecipientDocFields(options?.buyerCuit);

  const codAut = parseAfipQrCodAut(parsed.codAut ?? options?.cae);
  const fecha = resolveAfipQrFecha(parsed, options);

  return {
    ver: Number(parsed.ver ?? 1),
    fecha,
    cuit: Number(String(parsed.cuit ?? '').replace(/\D/g, '')),
    ptoVta: Number(parsed.ptoVta ?? 0),
    tipoCmp: Number(parsed.tipoCmp ?? 0),
    nroCmp: Number(parsed.nroCmp ?? 0),
    importe: typeof parsed.importe === 'number' ? parsed.importe : Number(parsed.importe ?? 0),
    moneda: String(parsed.moneda ?? 'PES'),
    ctz: Number(parsed.ctz ?? 1),
    tipoDocRec: recipient.tipoDocRec,
    nroDocRec: recipient.nroDocRec,
    tipoCodAut: String(parsed.tipoCodAut ?? 'E'),
    codAut,
  };
}

/** Serializa el payload con el orden y tipos exigidos por AFIP (RG 4291). */
export function serializeAfipQrPayload(record: AfipQrPayloadRecord): string {
  return JSON.stringify({
    ver: record.ver,
    fecha: record.fecha,
    cuit: record.cuit,
    ptoVta: record.ptoVta,
    tipoCmp: record.tipoCmp,
    nroCmp: record.nroCmp,
    importe: record.importe,
    moneda: record.moneda,
    ctz: record.ctz,
    tipoDocRec: record.tipoDocRec,
    nroDocRec: record.nroDocRec,
    tipoCodAut: record.tipoCodAut,
    codAut: parseAfipQrCodAut(record.codAut),
  });
}

/** Normaliza payloads viejos: doc receptor, CAE numérico y fecha local AR. */
export function normalizeAfipQrPayloadJson(
  payloadJson: string,
  options?: BuildAfipQrUrlOptions
): string {
  try {
    const parsed = JSON.parse(payloadJson) as AfipQrPayloadRecord;
    const normalized = normalizeAfipQrPayloadRecord(parsed, options);
    return serializeAfipQrPayload(normalized);
  } catch {
    return payloadJson;
  }
}

function decodeBase64Utf8(value: string): string | null {
  try {
    if (typeof atob === 'undefined') return null;
    const binary = atob(value);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
}

export function extractAfipQrPayloadFromUrl(qrUrl?: string | null): string | null {
  const raw = qrUrl?.trim();
  if (!raw) return null;
  try {
    const parsed = new URL(raw);
    const encoded = parsed.searchParams.get('p');
    if (!encoded) return null;
    return decodeBase64Utf8(encoded);
  } catch {
    const match = raw.match(/[?&]p=([^&]+)/);
    if (!match?.[1]) return null;
    return decodeBase64Utf8(decodeURIComponent(match[1]));
  }
}

export function buildAfipQrUrl(
  qrPayload?: string | null,
  qrUrlOverride?: string | null,
  options?: BuildAfipQrUrlOptions | null
): string | null {
  const resolvedOptions = options ?? {};
  const payloadFromArg = qrPayload?.trim();
  const payloadFromUrl = payloadFromArg ? null : extractAfipQrPayloadFromUrl(qrUrlOverride);
  const payload = payloadFromArg ?? payloadFromUrl;
  if (!payload) {
    return qrUrlOverride?.trim() || null;
  }
  try {
    const normalized = normalizeAfipQrPayloadJson(payload, resolvedOptions);
    return `${AFIP_QR_BASE}${encodeBase64Utf8(normalized)}`;
  } catch {
    return qrUrlOverride?.trim() || null;
  }
}

export function formatFiscalMoney(value: number): string {
  return `$${value.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function formatFiscalCuit(value?: string | null): string | null {
  const digits = value?.replace(/\D/g, '') ?? '';
  if (digits.length !== 11) return value?.trim() || null;
  return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`;
}

export function formatComprobanteNumber(
  pointOfSale?: number | null,
  invoiceNumber?: number | null
): string | null {
  if (pointOfSale == null || invoiceNumber == null) return null;
  const pv = String(pointOfSale).padStart(5, '0');
  const nro = String(invoiceNumber).padStart(8, '0');
  return `${pv}-${nro}`;
}

export function buildFiscalComprobanteFilename(
  input: FiscalComprobanteExportInput,
  extension: 'pdf' | 'html' = 'pdf'
): string {
  const nro = formatComprobanteNumber(input.pointOfSale, input.invoiceNumber);
  const safeRef = input.saleRef.replace(/[^\w-]+/g, '');
  return `comprobante-${nro ?? safeRef}.${extension}`;
}

export function escapeFiscalHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
